import React, { useState } from 'react';
import { useLang } from '../context/LanguageContext';
import { useUser } from '../context/UserContext';
import {
  getStudentsReport, getRevenueReport, getAttendanceReport,
  getGradesReport, getLecturersReport, getLeadsReport
} from '../services/api';
import { exportTableToPDF } from '../utils/pdfExport';

type ReportType = 'students' | 'revenue' | 'attendance' | 'grades' | 'lecturers' | 'leads' | null;

export default function Reports() {
  const { t } = useLang();
  const { user } = useUser();
  const isAdmin = user.role === 'admin';
  const [activeReport, setActiveReport] = useState<ReportType>(null);
  const [reportData, setReportData] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const loadReport = async (type: ReportType) => {
    if (!type) return;
    setLoading(true);
    setActiveReport(type);
    try {
      let res;
      switch (type) {
        case 'students': res = await getStudentsReport(); break;
        case 'revenue': res = await getRevenueReport(); break;
        case 'attendance': res = await getAttendanceReport(); break;
        case 'grades': res = await getGradesReport(); break;
        case 'lecturers': res = await getLecturersReport(); break;
        case 'leads': res = await getLeadsReport(); break;
      }
      setReportData(res?.data);
    } catch { }
    setLoading(false);
  };

  const allReports = [
    { type: 'students' as ReportType, icon: '🎓', title: t('reportStudents'), desc: 'רשימת כל הסטודנטים עם פרטים מלאים' },
    { type: 'revenue' as ReportType, icon: '💰', title: t('reportRevenue'), desc: 'דוח הכנסות וחובות מפורט', adminOnly: true },
    { type: 'attendance' as ReportType, icon: '📋', title: t('reportAttendance'), desc: 'סיכום נוכחות לפי קורסים' },
    { type: 'grades' as ReportType, icon: '📝', title: t('reportGrades'), desc: 'ציוני סטודנטים בכל הקורסים' },
    { type: 'lecturers' as ReportType, icon: '👨‍🏫', title: t('reportLecturers'), desc: 'מרצים, קורסים ושעות הוראה' },
    { type: 'leads' as ReportType, icon: '🎯', title: t('reportLeads'), desc: 'סטטיסטיקות לידים ושיעורי המרה' },
  ];
  const reports = allReports.filter(r => !r.adminOnly || isAdmin);

  return (
    <div>
      <div className="page-title">
        <span className="page-title-text">📈 {t('reports')}</span>
      </div>

      {!activeReport && (
        <div className="grid-3">
          {reports.map(r => (
            <div key={r.type} className="report-card" onClick={() => loadReport(r.type)}>
              <div className="icon">{r.icon}</div>
              <h3>{r.title}</h3>
              <p>{r.desc}</p>
            </div>
          ))}
        </div>
      )}

      {activeReport && (
        <div>
          <div style={{ marginBottom: 16 }}>
            <button className="btn btn-outline" onClick={() => { setActiveReport(null); setReportData(null); }}>← {t('back')}</button>
            <button className="btn btn-secondary" style={{ marginRight: 8 }}>{t('exportExcel')} 📥</button>
            <button
              className="btn"
              style={{ backgroundColor: '#B71C1C', color: '#fff', border: 'none', marginRight: 8 }}
              onClick={() => {
                if (!reportData || !activeReport) return;
                const date = new Date().toISOString().split('T')[0];
                let headers: string[] = [];
                let rows: string[][] = [];
                let title = '';
                if (activeReport === 'students') {
                  title = t('reportStudents');
                  headers = [t('name'), t('email'), t('phone'), t('status'), t('courses')];
                  rows = reportData.map((s: any) => [
                    `${s.firstName} ${s.lastName}`, s.email, s.phone, s.status, s.courseNames?.join(', ') || '-'
                  ]);
                } else if (activeReport === 'revenue') {
                  title = t('reportRevenue');
                  headers = [t('students'), t('courses'), t('amount'), t('paidAmount'), t('status')];
                  rows = (reportData.payments || []).map((p: any) => [
                    p.studentName, p.courseName, `${p.amount?.toLocaleString()} ₪`, `${p.paidAmount?.toLocaleString()} ₪`, p.status
                  ]);
                } else if (activeReport === 'attendance') {
                  title = t('reportAttendance');
                  headers = [t('students'), t('courses'), t('date'), t('status')];
                  rows = reportData.map((a: any) => [
                    a.studentName, a.courseName, a.date, a.status === 'present' ? 'נוכח' : 'נעדר'
                  ]);
                } else if (activeReport === 'grades') {
                  title = t('reportGrades');
                  headers = [t('students'), t('courses'), 'סוג בחינה', 'ציון', t('date')];
                  rows = reportData.map((g: any) => [
                    g.studentName, g.courseName, g.examType, String(g.grade), g.date
                  ]);
                } else if (activeReport === 'lecturers') {
                  title = t('reportLecturers');
                  headers = [t('name'), t('specialization'), t('courses'), 'שעות שבועיות', t('hourlyRate')];
                  rows = reportData.map((l: any) => [
                    `${l.firstName} ${l.lastName}`, l.specialization, l.courseNames?.join(', ') || '-', String(l.totalHours), `${l.hourlyRate} ₪`
                  ]);
                } else if (activeReport === 'leads') {
                  title = t('reportLeads');
                  headers = ['קטגוריה', 'ערך'];
                  rows = [
                    ['סה"כ לידים', String(reportData.total)],
                    ['שיעור המרה', `${reportData.conversionRate}%`],
                    ...Object.entries(reportData.stages || {}).map(([stage, count]) => [t(`stages.${stage}`), String(count)]),
                    ...Object.entries(reportData.sources || {}).map(([source, count]) => [t(`sources.${source}`), String(count)]),
                  ];
                }
                if (headers.length > 0) {
                  exportTableToPDF(title, headers, rows, `report_${activeReport}_${date}.pdf`);
                }
              }}
            >{t('exportPDF')} 📄</button>
          </div>

          {loading ? <div className="page-section"><p>{t('loading')}</p></div> : (
            <div className="page-section">
              {activeReport === 'students' && reportData && (
                <div>
                  <h3 style={{ color: '#7B1FA2', marginBottom: 16 }}>{t('reportStudents')}</h3>
                  <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('name')}</th>
                        <th>{t('email')}</th>
                        <th>{t('phone')}</th>
                        <th>{t('status')}</th>
                        <th>{t('courses')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.map((s: any) => (
                        <tr key={s.id}>
                          <td>{s.firstName} {s.lastName}</td>
                          <td dir="ltr">{s.email}</td>
                          <td dir="ltr">{s.phone}</td>
                          <td><span className={`badge ${s.status === 'active' ? 'badge-green' : 'badge-gray'}`}>{s.status}</span></td>
                          <td>{s.courseNames?.join(', ') || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}

              {activeReport === 'revenue' && reportData && (
                <div>
                  <h3 style={{ color: '#7B1FA2', marginBottom: 16 }}>{t('reportRevenue')}</h3>
                  <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: 20 }}>
                    <div className="stat-card">
                      <div className="value">{reportData.totalRevenue?.toLocaleString()} ₪</div>
                      <div className="label">{t('totalRevenue')}</div>
                    </div>
                    <div className="stat-card" style={{ borderRightColor: '#dc3545' }}>
                      <div className="value" style={{ color: '#dc3545' }}>{reportData.totalDebt?.toLocaleString()} ₪</div>
                      <div className="label">{t('totalDebt')}</div>
                    </div>
                  </div>
                  <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('students')}</th>
                        <th>{t('courses')}</th>
                        <th>{t('amount')}</th>
                        <th>{t('paidAmount')}</th>
                        <th>{t('status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.payments?.map((p: any) => (
                        <tr key={p.id}>
                          <td>{p.studentName}</td>
                          <td>{p.courseName}</td>
                          <td>{p.amount?.toLocaleString()} ₪</td>
                          <td>{p.paidAmount?.toLocaleString()} ₪</td>
                          <td><span className={`badge ${p.status === 'paid' ? 'badge-green' : p.status === 'partial' ? 'badge-yellow' : 'badge-red'}`}>{p.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}

              {activeReport === 'attendance' && reportData && (
                <div>
                  <h3 style={{ color: '#7B1FA2', marginBottom: 16 }}>{t('reportAttendance')}</h3>
                  <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('students')}</th>
                        <th>{t('courses')}</th>
                        <th>{t('date')}</th>
                        <th>{t('status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.map((a: any) => (
                        <tr key={a.id}>
                          <td>{a.studentName}</td>
                          <td>{a.courseName}</td>
                          <td>{a.date}</td>
                          <td><span className={`badge ${a.status === 'present' ? 'badge-green' : 'badge-red'}`}>
                            {a.status === 'present' ? 'נוכח' : 'נעדר'}
                          </span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}

              {activeReport === 'grades' && reportData && (
                <div>
                  <h3 style={{ color: '#7B1FA2', marginBottom: 16 }}>{t('reportGrades')}</h3>
                  <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('students')}</th>
                        <th>{t('courses')}</th>
                        <th>סוג בחינה</th>
                        <th>ציון</th>
                        <th>{t('date')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.map((g: any) => (
                        <tr key={g.id}>
                          <td>{g.studentName}</td>
                          <td>{g.courseName}</td>
                          <td>{g.examType}</td>
                          <td><strong style={{ color: g.grade >= 60 ? '#7B1FA2' : '#dc3545' }}>{g.grade}</strong></td>
                          <td>{g.date}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}

              {activeReport === 'lecturers' && reportData && (
                <div>
                  <h3 style={{ color: '#7B1FA2', marginBottom: 16 }}>{t('reportLecturers')}</h3>
                  <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>{t('name')}</th>
                        <th>{t('specialization')}</th>
                        <th>{t('courses')}</th>
                        <th>שעות שבועיות</th>
                        <th>{t('hourlyRate')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.map((l: any) => (
                        <tr key={l.id}>
                          <td>{l.firstName} {l.lastName}</td>
                          <td>{l.specialization}</td>
                          <td>{l.courseNames?.join(', ') || '-'}</td>
                          <td>{l.totalHours}</td>
                          <td>{l.hourlyRate} ₪</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}

              {activeReport === 'leads' && reportData && (
                <div>
                  <h3 style={{ color: '#7B1FA2', marginBottom: 16 }}>{t('reportLeads')}</h3>
                  <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)', marginBottom: 20 }}>
                    <div className="stat-card">
                      <div className="value">{reportData.total}</div>
                      <div className="label">סה"כ לידים</div>
                    </div>
                    <div className="stat-card" style={{ borderRightColor: '#81D4FA' }}>
                      <div className="value" style={{ color: '#81D4FA' }}>{reportData.conversionRate}%</div>
                      <div className="label">שיעור המרה</div>
                    </div>
                    <div className="stat-card">
                      <div className="value">{reportData.stages?.active_student || 0}</div>
                      <div className="label">הומרו לסטודנטים</div>
                    </div>
                  </div>
                  <h4 style={{ marginBottom: 12, color: '#333' }}>לפי שלב:</h4>
                  <div className="stats-grid">
                    {Object.entries(reportData.stages || {}).map(([stage, count]) => (
                      <div key={stage} className="stat-card" style={{ borderRightColor: '#81D4FA' }}>
                        <div className="value" style={{ fontSize: 20 }}>{count as number}</div>
                        <div className="label">{t(`stages.${stage}`)}</div>
                      </div>
                    ))}
                  </div>
                  <h4 style={{ marginTop: 20, marginBottom: 12, color: '#333' }}>לפי מקור:</h4>
                  <div className="stats-grid">
                    {Object.entries(reportData.sources || {}).map(([source, count]) => (
                      <div key={source} className="stat-card">
                        <div className="value" style={{ fontSize: 20 }}>{count as number}</div>
                        <div className="label">{t(`sources.${source}`)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
